import * as z from "zod";
import { returnsAnalyticsDataQualitySchema } from "./returns-analytics-shared";

export const cohortGranularitySchema = z.enum(["week", "month"]);
export type CohortGranularity = z.infer<typeof cohortGranularitySchema>;

export const getReturnsCohortRetentionInputSchema = z.object({
  granularity: cohortGranularitySchema.default("week"),
  cohortCount: z.number().int().min(1).max(52).default(12),
  /**
   * Usage threshold for "high-usage" segmentation on the retention
   * curve. Teams with `returns_30d >= threshold` are high-usage;
   * below are low-usage.
   */
  usageSegmentThreshold: z.number().int().min(0).default(10),
});
export type GetReturnsCohortRetentionInput = z.infer<
  typeof getReturnsCohortRetentionInputSchema
>;

export const cohortRowSchema = z.object({
  cohortStart: z.date(),
  cohortSize: z.number().int().min(0),
  activeNow: z.number().int().min(0),
  churnedOnce: z.number().int().min(0),
  reactivated: z.number().int().min(0),
  retentionPct: z.number().min(0).max(100),
  avgTimeToValueDays: z.number().min(0).nullable(),
});
export type CohortRow = z.infer<typeof cohortRowSchema>;

/**
 * Per-cohort retention at fixed checkpoints (0, 30, 60, 90 days).
 * Each returns_30d bucket is segmented separately so the UI can draw
 * two curves ("high-usage" vs "low-usage" retention).
 */
export const retentionCheckpointSchema = z.object({
  cohortStart: z.date(),
  daysSinceCohortStart: z.number().int().min(0),
  retentionPct: z.number().min(0).max(100),
  cohortSize: z.number().int().min(0),
});
export type RetentionCheckpoint = z.infer<typeof retentionCheckpointSchema>;

/**
 * Weekly KPI trend point — powers the Scorecard sparkline. One entry
 * per week in the retention window, showing % of closed-won deals in
 * that week that also completed self-serve.
 */
export const kpiTrendPointSchema = z.object({
  weekStart: z.date(),
  closedWonCount: z.number().int().min(0),
  viaSelfServeCount: z.number().int().min(0),
  pctViaSelfServe: z.number().min(0).max(100),
});
export type KpiTrendPoint = z.infer<typeof kpiTrendPointSchema>;

export const getReturnsCohortRetentionOutputSchema = z.object({
  cohorts: z.array(cohortRowSchema),
  checkpoints: z.array(retentionCheckpointSchema),
  highUsageCheckpoints: z.array(retentionCheckpointSchema),
  lowUsageCheckpoints: z.array(retentionCheckpointSchema),
  kpiTrend: z.array(kpiTrendPointSchema),
  dataQuality: returnsAnalyticsDataQualitySchema,
});
export type GetReturnsCohortRetentionOutput = z.infer<
  typeof getReturnsCohortRetentionOutputSchema
>;

export const getReturnsCohortRetention = {
  name: "getReturnsCohortRetention" as const,
  input: getReturnsCohortRetentionInputSchema,
  output: getReturnsCohortRetentionOutputSchema,
};
